import React, { useRef, useState, useEffect } from "react";
import { Button, Col, Container, Row } from "react-bootstrap";
import { usePreventZoom } from "../utils/usePreventZoom.tsx";
import { useApiFetch, backendURL } from "../../../utils/api";
import "./TargetScanner.css"
import { CenterFieldDto, HitDto, TargetResultDto } from "../../../types/TargetScannedDataDTOs.ts";
import { CircleMarker } from "../../../types/CircleMarker.ts";
import { calculateScore, calculateTotalScoreSum } from "../../../utils/calculateScore.ts";
import { ShootingEventConfig } from "../../../types/ShootingEventConfig.ts";
import { TargetConfig } from "../../../types/TargetConfig.ts";
import { calculateDistance } from "../../../utils/statsCalculation.ts";
import { useConfirmation } from "../../../contexts/ConfirmationContext.tsx";
import { AcknowledgedTargetsTrainingScanResponse } from "../../../types/TrainingScan.ts";
import { buildAcknowledgedTargetsTrainingScanRequest, getImageDimensions } from "../utils/trainingScanAcknowledgement.ts";
import { useInfoDialog } from "../../../contexts/InfoDialogContext.tsx";
import { useNotifications } from "../../../contexts/NotificationContext.tsx";
import {
    buildHitsScannerV2Request,
    getHitMarkerStatusClassName,
    getHitsScannerErrorMessage,
    HitDetectionStatus,
    HitsScannerV2Response,
    mapHitDetectionsToMarkers
} from "../utils/hitsScannerV2.ts";
import {
    getNextScannerStep,
    getPreviousScannerStep,
    restoreHitMarkersWithRadius,
    ScannerStep
} from "../utils/scannerWorkflow.ts";

interface TargetScannerProps {
    targetImages: string[];
    shotsPerTarget: number;
    setTargetScannerRendered: React.Dispatch<React.SetStateAction<boolean>>;
    setTargetResults: React.Dispatch<React.SetStateAction<Array<TargetResultDto>>>;
    targetResults: Array<TargetResultDto>;
    hitRadius: number;
    testShotsPerTarget: number;
    competitionMode: boolean;
    shootingEventConfig: ShootingEventConfig;
    onBackToSessionData: (confirmDiscard?: boolean) => Promise<boolean>;
    isActive: boolean;
}

interface HitMarker extends CircleMarker {
    newlyManuallyAdded: boolean;
    detectionStatus?: HitDetectionStatus;
    confidence?: number;
}

interface TargetScanDraft {
    centerFieldMarkers: CircleMarker[];
    hitsMarkers: HitMarker[];
    markersDrawingScaleFactor: number;
    centerFieldScanningSensivityOffset: number;
    pointsScored: string[];
    hitDetectionError: string | null;
    hitDetectorModelVersion: string | null;
}

interface CenterFieldDraft {
    centerFieldMarkers: CircleMarker[];
    markersDrawingScaleFactor: number;
    centerFieldScanningSensivityOffset: number;
}

const defaultScanningSensitivity: number = 0;
const numberOfDecimalPlacesOfHitCoordinates: number = 3;
const touchHitPlacementMoveThresholdInPixels: number = 3;

const TargetScanner: React.FC<TargetScannerProps> = (props: TargetScannerProps) => {
    const apiFetch = useApiFetch();
    const { confirm } = useConfirmation();
    const { showInfo } = useInfoDialog();
    const { error: showErrorToast } = useNotifications();
    usePreventZoom(props.isActive, props.isActive);

    const targetConfig: TargetConfig = props.shootingEventConfig.targetConfig;

    const [centerFieldMarkers, setCenterFieldMarkers] = useState<CircleMarker[]>([]);
    const [scannerStep, setScannerStep] = useState<ScannerStep>({ phase: "center-field", targetIndex: 0 });
    const centerFieldMarkersRendered = scannerStep.phase === "center-field";
    const currentImageIndex = scannerStep.targetIndex;

    const [hitsMarkers, setHitsMarkers] = useState<HitMarker[]>([]);
    const [hitMarkerPlacementMode, setHitMarkerPlacementMode] = useState<boolean>(false);

    const [markersDrawingScaleFactor, setMarkersDrawingScaleFactor] = useState<number>(1.0)

    const [dragable, setDragable] = useState<boolean>(false);

    const targetImageRef = useRef<SVGImageElement>(null);
    const svgRef = useRef<SVGSVGElement>(null);
    const pendingTouchHitPlacementRef = useRef<HitDto | null>(null);
    const pendingTouchHitPlacementCanceledRef = useRef<boolean>(false);

    const [selectedHitMarkerIndex, setSelectedHitMarkerIndex] = useState<number>(-1);
    const [selectedHitMarkerPoints, setSelectedHitMarkerPoints] = useState<string>("0");

    const detectCenterFieldTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const centerFieldRequestIdRef = useRef<number>(0);
    const hitsRequestIdRef = useRef<number>(0);
    const targetScanDraftsRef = useRef<Array<TargetScanDraft | undefined>>([]);
    const centerFieldDraftsRef = useRef<Array<CenterFieldDraft | undefined>>([]);
    const scanReviewStartedRef = useRef<boolean>(false);

    const [centerFieldScanningSensivityOffset, setCenterFieldScanningSensivityOffset] = useState<number>(defaultScanningSensitivity);

    const [pointsScored, setPointsScored] = useState<string[]>([]);
    const [hitDetectionError, setHitDetectionError] = useState<string | null>(null);
    const [hitDetectorModelVersion, setHitDetectorModelVersion] = useState<string | null>(null);

    const targetImageScanningWidth = 1100.0
    const innerCenterFieldMarkerScale: number = 0.08;

    useEffect(() => {
        if (centerFieldMarkersRendered) {
            detectCenterField(props.targetImages[currentImageIndex])
        }
    }, []);

    useEffect(() => {
        return () => {
            if (detectCenterFieldTimeoutRef.current) {
                clearTimeout(detectCenterFieldTimeoutRef.current);
            }
            centerFieldRequestIdRef.current += 1;
            hitsRequestIdRef.current += 1;
        };
    }, []);

    useEffect(() => {
        const preventDefaultTouch = (event: TouchEvent) => {
            if (dragable == true) {
                event.preventDefault();
            }

        };
        window.addEventListener("touchmove", preventDefaultTouch, { passive: false });

        return () => {
            window.removeEventListener("touchmove", preventDefaultTouch);
        };
    }, [dragable]);

    useEffect(() => {
        calculatePointsForSelectedHitMarker();
    }, [selectedHitMarkerIndex]);

    useEffect(() => {
        if (hitsMarkers.length != 0) {
            calculateAndSetResult();
        }
    }, [hitsMarkers])

    const getHitMarkersSortedByDistanceFromCenter = (): HitMarker[] => {
        const sortedHitsMarkers = [...hitsMarkers].sort((hitA, hitB) => {
            return calculateDistance(hitA, getCenterFieldOuterMarker()) - calculateDistance(hitB, getCenterFieldOuterMarker());
        });

        return sortedHitsMarkers;
    };

    const handleMove = (event: React.MouseEvent<SVGSVGElement, MouseEvent> | React.TouchEvent<SVGSVGElement>) => {
        if (dragable == true) {
            const clientX = 'touches' in event ? event.touches[0].clientX : event.clientX;
            const clientY = 'touches' in event ? event.touches[0].clientY : event.clientY;

            const svgRect = targetImageRef.current?.getBoundingClientRect();
            if (svgRect) {
                const newX = clientX - svgRect.left;
                const newY = clientY - svgRect.top;

                if (centerFieldMarkersRendered) {
                    const updatedCirclesMarkers: CircleMarker[] = centerFieldMarkers.map((circleMarker: CircleMarker) => ({ x: newX, y: newY, radius: circleMarker.radius }));
                    setCenterFieldMarkers(updatedCirclesMarkers);
                } else {
                    const updatedHitMarkers: HitMarker[] = hitsMarkers.map((hitMarker: HitMarker, index: number) => {
                        if (index == selectedHitMarkerIndex) {
                            return { ...hitMarker, x: newX, y: newY };
                        } else {
                            return hitMarker;
                        }
                    })
                    setHitsMarkers(updatedHitMarkers);
                }

            }
        }
    };

    const getTargetImagePoint = (clientX: number, clientY: number): HitDto | null => {
        const targetImageRect = targetImageRef.current?.getBoundingClientRect();

        if (!targetImageRect) {
            return null;
        }

        const x = clientX - targetImageRect.left;
        const y = clientY - targetImageRect.top;

        if (x < 0 || y < 0 || x > targetImageRect.width || y > targetImageRect.height) {
            return null;
        }

        return { x: x, y: y };
    }

    const addHitMarkerFromClientPoint = (clientX: number, clientY: number): boolean => {
        const point = getTargetImagePoint(clientX, clientY);

        if (!point) {
            return false;
        }

        addHitMarker(point.x, point.y);
        return true;
    };

    const handleTargetMouseDown = (event: React.MouseEvent<SVGSVGElement, MouseEvent>) => {
        if (!hitMarkerPlacementMode || centerFieldMarkersRendered) {
            return;
        }

        if (addHitMarkerFromClientPoint(event.clientX, event.clientY)) {
            event.preventDefault();
        }
    }

    const handleTargetTouchStart = (event: React.TouchEvent<SVGSVGElement>) => {
        pendingTouchHitPlacementRef.current = null;
        pendingTouchHitPlacementCanceledRef.current = false;

        if (!hitMarkerPlacementMode || centerFieldMarkersRendered || event.touches.length !== 1) {
            pendingTouchHitPlacementCanceledRef.current = true;
            return;
        }

        const touch = event.touches[0];
        pendingTouchHitPlacementRef.current = { x: touch.clientX, y: touch.clientY };
    }

    const handleTargetTouchMove = (event: React.TouchEvent<SVGSVGElement>) => {
        if (!hitMarkerPlacementMode || !pendingTouchHitPlacementRef.current || event.touches.length !== 1) {
            pendingTouchHitPlacementCanceledRef.current = true;
            handleMove(event);
            return;
        }

        const touch = event.touches[0];
        const movedDistance = Math.hypot(
            touch.clientX - pendingTouchHitPlacementRef.current.x,
            touch.clientY - pendingTouchHitPlacementRef.current.y
        );

        if (movedDistance > touchHitPlacementMoveThresholdInPixels) {
            pendingTouchHitPlacementCanceledRef.current = true;
        }

        handleMove(event);
    }

    const handleTargetTouchEnd = (event: React.TouchEvent<SVGSVGElement>) => {
        if (hitMarkerPlacementMode && pendingTouchHitPlacementRef.current && !pendingTouchHitPlacementCanceledRef.current) {
            if (addHitMarkerFromClientPoint(pendingTouchHitPlacementRef.current.x, pendingTouchHitPlacementRef.current.y)) {
                event.preventDefault();
            }
        }

        pendingTouchHitPlacementRef.current = null;
        pendingTouchHitPlacementCanceledRef.current = false;
        handleMouseUp();
    }

    const handleTargetTouchCancel = () => {
        pendingTouchHitPlacementRef.current = null;
        pendingTouchHitPlacementCanceledRef.current = false;
        handleMouseUp();
    }


    const centerFieldMarkerHandleMouseDown = (event: React.MouseEvent<SVGCircleElement, MouseEvent> | React.TouchEvent<SVGCircleElement>) => {
        event.stopPropagation();
        setDragable(true);
    };

    const hitMarkerHandleMouseDown = (index: number) => (event: React.MouseEvent<SVGCircleElement, MouseEvent> | React.TouchEvent<SVGCircleElement>) => {
        event.stopPropagation();
        setDragable(true);
        setHitMarkerPlacementMode(false);
        setSelectedHitMarkerIndex(index);
    };

    const handleMouseUp = () => {
        setDragable(false);
    };

    const detectCenterField = async (image: string) => {
        const requestId = ++centerFieldRequestIdRef.current;

        try {
            const response: CenterFieldDto = await apiFetch(`${backendURL}/center-field-scanner`, {
                method: "POST",
                body: JSON.stringify({
                    image: image,
                    scanningSensitivityOffset: centerFieldScanningSensivityOffset,
                    targetId: targetConfig.targetId
                }),
                includeAuth: true
            });

            if (requestId === centerFieldRequestIdRef.current && targetImageRef.current) {
                const scaleFactor = targetImageRef.current?.width.baseVal.value / targetImageScanningWidth

                const x: number = response.x * scaleFactor;
                const y: number = response.y * scaleFactor;
                const radius: number = response.radius * scaleFactor;

                const outerMarker: CircleMarker = { x: x, y: y, radius: radius };
                const innerMarker: CircleMarker = { x: x, y: y, radius: radius * innerCenterFieldMarkerScale };

                setCenterFieldMarkers([outerMarker, innerMarker]);
                setMarkersDrawingScaleFactor(scaleFactor);
            }
        } catch (error) {
            console.info("Exception while executing detectCenterField method", error);
        }
    };

    const detectHits = async (image: string) => {
        const requestId = ++hitsRequestIdRef.current;
        setSelectedHitMarkerIndex(-1);
        setHitDetectionError(null);
        setHitDetectorModelVersion(null);
        const centerFieldOuterMarker: CircleMarker = getCenterFieldOuterMarker();

        try {
            const request = buildHitsScannerV2Request({
                image: image,
                targetId: targetConfig.targetId,
                renderedCenterField: centerFieldOuterMarker,
                markersDrawingScaleFactor: markersDrawingScaleFactor,
                targetCenterFieldRadius: targetConfig.centerFieldRadius,
                hitRadius: props.hitRadius
            });
            const response: HitsScannerV2Response = await apiFetch(`${backendURL}/v2/hits-scanner`, {
                method: "POST",
                body: JSON.stringify(request),
                includeAuth: true,
                showErrorToast: false
            });

            if (requestId === hitsRequestIdRef.current && targetImageRef.current) {
                const scaleFactor = targetImageRef.current?.width.baseVal.value / targetImageScanningWidth;
                const hitMarkerRadius: number = centerFieldOuterMarker.radius / targetConfig.centerFieldRadius * props.hitRadius
                const hitsMarkers: HitMarker[] = mapHitDetectionsToMarkers(response, scaleFactor, hitMarkerRadius);

                setHitsMarkers(limitAutoDetectedHitMarkers(hitsMarkers, centerFieldOuterMarker));
                setHitMarkerPlacementMode(false);
                setHitDetectorModelVersion(response.modelVersion);
            }
        } catch (error) {
            console.info("Exception while executing detectHits method", error);
            const errorMessage = getHitsScannerErrorMessage(error);
            if (requestId === hitsRequestIdRef.current) {
                setHitsMarkers([]);
                setHitDetectionError(errorMessage);
                showErrorToast(errorMessage);
            }
        }
    };

    const limitAutoDetectedHitMarkers = (hitMarkers: HitMarker[], centerFieldOuterMarker: CircleMarker): HitMarker[] => {
        const maximumAutoDetectedHitMarkers = props.shotsPerTarget + 5;

        if (hitMarkers.length <= maximumAutoDetectedHitMarkers) {
            return hitMarkers;
        }

        return [...hitMarkers]
            .sort((hitA, hitB) => calculateDistance(hitA, centerFieldOuterMarker) - calculateDistance(hitB, centerFieldOuterMarker))
            .slice(0, maximumAutoDetectedHitMarkers);
    };

    const getHitMarkerRadius = (): number => {
        return getCenterFieldOuterMarker().radius / targetConfig.centerFieldRadius * props.hitRadius;
    }

    const startHitMarkerPlacement = () => {
        setSelectedHitMarkerIndex(-1);
        setHitMarkerPlacementMode(true);
    };

    const toggleHitMarkerPlacement = () => {
        if (hitMarkerPlacementMode) {
            setHitMarkerPlacementMode(false);
        } else {
            startHitMarkerPlacement();
        }
    };

    const addHitMarker = (x: number, y: number) => {
        const newHitMarkerIndex = hitsMarkers.length;
        setHitsMarkers([...hitsMarkers, {
            x: x, y: y, radius: getHitMarkerRadius(), newlyManuallyAdded: true
        }]);
        setSelectedHitMarkerIndex(newHitMarkerIndex);
        setHitMarkerPlacementMode(false);
    };

    const deleteHitMarker = () => {
        setSelectedHitMarkerIndex(-1);
        setHitMarkerPlacementMode(false);

        const arrayCopy = [...hitsMarkers];
        arrayCopy.splice(selectedHitMarkerIndex, 1);

        setHitsMarkers(arrayCopy);

        if (arrayCopy.length === 0) {
            setPointsScored([]);
        }
    };

    const deleteAllHitMarkers = async () => {
        if (hitsMarkers.length === 0) {
            return;
        }

        const proceed: boolean = await confirm({
            message: "Czy na pewno chcesz usunąć wszystkie oznaczenia trafień?",
            confirmLabel: "Usuń wszystkie",
        });

        if (!proceed) {
            return;
        }

        setSelectedHitMarkerIndex(-1);
        setHitMarkerPlacementMode(false);
        setHitsMarkers([]);
        setPointsScored([]);
    };

    const getCenterFieldOuterMarker = (): CircleMarker => {
        return centerFieldMarkers.reduce((prev, current) => {
            return prev.radius > current.radius ? prev : current;
        })
    };

    const calculateAndSetResult = () => {
        const pointsScored: string[] = calculateScore(getCenterFieldOuterMarker(), hitsMarkers, targetConfig, props.hitRadius, getNumberOfRatedShots());
        setPointsScored(pointsScored);
        calculatePointsForSelectedHitMarker();
    }

    const calculatePointsForSelectedHitMarker = (): void => {
        if (selectedHitMarkerIndex != -1) {
            setSelectedHitMarkerPoints(calculateScore(getCenterFieldOuterMarker(), [hitsMarkers[selectedHitMarkerIndex]], targetConfig, props.hitRadius, getNumberOfRatedShots())[0]);
        }
    }

    const acknowledgeTargetsForTraining = async () => {
        const image = props.targetImages[currentImageIndex];
        const renderedImageRect = targetImageRef.current?.getBoundingClientRect();

        if (!renderedImageRect) {
            console.info("Skipping acknowledgeTargetsForTraining because rendered image dimensions are unavailable");
            return;
        }

        try {
            const imageDimensions = await getImageDimensions(image);
            const request = buildAcknowledgedTargetsTrainingScanRequest({
                image: image,
                targetId: targetConfig.targetId,
                centerField: getCenterFieldOuterMarker(),
                acceptedHitMarkers: hitsMarkers,
                renderedImageDimensions: {
                    width: renderedImageRect.width,
                    height: renderedImageRect.height
                },
                storedImageDimensions: imageDimensions,
                shootingEventId: props.shootingEventConfig.shootingEventId
            });

            await apiFetch<AcknowledgedTargetsTrainingScanResponse>(`${backendURL}/training-scans/acknowledged-targets`, {
                method: "POST",
                body: JSON.stringify(request),
                includeAuth: true,
                showErrorToast: false
            });
        } catch (error) {
            console.info("Exception while executing acknowledgeTargetsForTraining method", error);
        }
    };

    const createCurrentTargetDraft = (): TargetScanDraft => ({
        centerFieldMarkers: centerFieldMarkers.map((marker) => ({ ...marker })),
        hitsMarkers: hitsMarkers.map((marker) => ({ ...marker })),
        markersDrawingScaleFactor,
        centerFieldScanningSensivityOffset,
        pointsScored: [...pointsScored],
        hitDetectionError,
        hitDetectorModelVersion
    });

    const createCurrentCenterFieldDraft = (): CenterFieldDraft => ({
        centerFieldMarkers: centerFieldMarkers.map((marker) => ({ ...marker })),
        markersDrawingScaleFactor,
        centerFieldScanningSensivityOffset
    });

    const restoreTargetDraft = (draft: TargetScanDraft) => {
        setCenterFieldMarkers(draft.centerFieldMarkers.map((marker) => ({ ...marker })));
        setHitsMarkers(draft.hitsMarkers.map((marker) => ({ ...marker })));
        setMarkersDrawingScaleFactor(draft.markersDrawingScaleFactor);
        setCenterFieldScanningSensivityOffset(draft.centerFieldScanningSensivityOffset);
        setPointsScored([...draft.pointsScored]);
        setHitDetectionError(draft.hitDetectionError);
        setHitDetectorModelVersion(draft.hitDetectorModelVersion);
        setSelectedHitMarkerIndex(-1);
        setHitMarkerPlacementMode(false);
    };

    const restoreCenterFieldDraft = (draft: CenterFieldDraft) => {
        setCenterFieldMarkers(draft.centerFieldMarkers.map((marker) => ({ ...marker })));
        setMarkersDrawingScaleFactor(draft.markersDrawingScaleFactor);
        setCenterFieldScanningSensivityOffset(draft.centerFieldScanningSensivityOffset);
        setHitsMarkers([]);
        setPointsScored([]);
        setHitDetectionError(null);
        setHitDetectorModelVersion(null);
        setSelectedHitMarkerIndex(-1);
        setHitMarkerPlacementMode(false);
    };

    const restoreHitReviewDraft = (draft: TargetScanDraft) => {
        const hitMarkerRadius = getHitMarkerRadius();

        setHitsMarkers(restoreHitMarkersWithRadius(draft.hitsMarkers, hitMarkerRadius));
        setPointsScored([]);
        setHitDetectionError(draft.hitDetectionError);
        setHitDetectorModelVersion(draft.hitDetectorModelVersion);
        setSelectedHitMarkerIndex(-1);
        setHitMarkerPlacementMode(false);
    };

    const cancelPendingDetection = () => {
        if (detectCenterFieldTimeoutRef.current) {
            clearTimeout(detectCenterFieldTimeoutRef.current);
            detectCenterFieldTimeoutRef.current = null;
        }
        centerFieldRequestIdRef.current += 1;
        hitsRequestIdRef.current += 1;
    };

    const goBack = async () => {
        const previousStep = getPreviousScannerStep(scannerStep);

        if (previousStep.phase === "session-data") {
            const returnedToSessionData = await props.onBackToSessionData(scanReviewStartedRef.current);
            if (returnedToSessionData) {
                cancelPendingDetection();
            }
            return;
        }

        cancelPendingDetection();

        if (previousStep.phase === "center-field") {
            const hasReusableHitReview = hitsMarkers.length > 0 || hitDetectorModelVersion !== null;
            if (hasReusableHitReview) {
                targetScanDraftsRef.current[currentImageIndex] = createCurrentTargetDraft();
            } else {
                targetScanDraftsRef.current[currentImageIndex] = undefined;
            }

            setSelectedHitMarkerIndex(-1);
            setHitMarkerPlacementMode(false);
            setHitsMarkers([]);
            setPointsScored([]);
            setHitDetectionError(null);
            setHitDetectorModelVersion(null);
            setScannerStep(previousStep);
            return;
        }

        centerFieldDraftsRef.current[currentImageIndex] = centerFieldMarkers.length > 0
            ? createCurrentCenterFieldDraft()
            : undefined;

        const previousDraft = targetScanDraftsRef.current[previousStep.targetIndex];
        if (!previousDraft) {
            return;
        }

        restoreTargetDraft(previousDraft);
        setScannerStep(previousStep);
        props.setTargetResults((currentResults) => currentResults.slice(0, -1));
    };

    const goBackToSessionData = async () => {
        const returnedToSessionData = await props.onBackToSessionData(scanReviewStartedRef.current);
        if (returnedToSessionData) {
            cancelPendingDetection();
        }
    };

    const submit = async () => {
        if (centerFieldMarkersRendered === true) {
            const nextStep = getNextScannerStep(scannerStep, props.targetImages.length);
            if (nextStep.phase !== "hits") {
                return;
            }
            scanReviewStartedRef.current = true;
            setScannerStep(nextStep);
            centerFieldDraftsRef.current[currentImageIndex] = undefined;

            const existingDraft = targetScanDraftsRef.current[currentImageIndex];
            if (existingDraft) {
                restoreHitReviewDraft(existingDraft);
                return;
            }

            detectHits(props.targetImages[currentImageIndex]);
        } else {
            if (props.shotsPerTarget > hitsMarkers.length) {
                const proceed: boolean = await confirm({
                    message: "Liczba wykrytych trafień jest mniejsza od liczby strzałów. Czy chcesz kontynuować mimo to?",
                    confirmLabel: "Tak",
                });

                if (!proceed) {
                    return;
                }
            } else if (props.shotsPerTarget < hitsMarkers.length) {
                const proceed: boolean = await confirm({
                    message: `Liczba wykrytych trafień jest większa od liczby strzałów. Zostanie zapisane tylko ${getNumberOfRatedShots()} najlepszych strzałów. Czy chcesz kontynuować?`,
                    confirmLabel: "Tak",
                });

                if (!proceed) {
                    return;
                }
            }

            acknowledgeTargetsForTraining();

            const detectedHits = getHitMarkersSortedByDistanceFromCenter().slice(0, getNumberOfRatedShots());

            const coordinatesOfHitsRelativeToCenter: HitDto[] = [];
            detectedHits.forEach((detectedHit: HitMarker) => {
                const scaleFactor: number = getCenterFieldOuterMarker().radius / targetConfig.centerFieldRadius;
                coordinatesOfHitsRelativeToCenter.push(calculateCoordinatesOfHitRelativeToCenter(detectedHit, scaleFactor));
            })

            const targetResults: TargetResultDto = {
                coordinatesOfHitsRelativeToCenter: coordinatesOfHitsRelativeToCenter,
                listOfPointsScored: pointsScored
            }

            targetScanDraftsRef.current[currentImageIndex] = createCurrentTargetDraft();
            centerFieldDraftsRef.current[currentImageIndex] = undefined;
            props.setTargetResults([...props.targetResults, targetResults]);

            const nextStep = getNextScannerStep(scannerStep, props.targetImages.length);
            if (nextStep.phase === "report") {
                props.setTargetScannerRendered(false);
                return;
            }

            const existingCenterFieldDraft = centerFieldDraftsRef.current[nextStep.targetIndex];
            if (existingCenterFieldDraft) {
                restoreCenterFieldDraft(existingCenterFieldDraft);
                setScannerStep(nextStep);
                return;
            }

            const existingDraft = targetScanDraftsRef.current[nextStep.targetIndex];
            if (existingDraft) {
                restoreTargetDraft(existingDraft);
                setScannerStep({ phase: "hits", targetIndex: nextStep.targetIndex });
                return;
            }

            resetScannerToInitialState();
            setScannerStep(nextStep);
            detectCenterField(props.targetImages[nextStep.targetIndex]);
        }
    };

    const calculateCoordinatesOfHitRelativeToCenter = (hit: HitMarker, scaleFactor: number): HitDto => {
        return {
            x: parseFloat(((hit.x - centerFieldMarkers[0].x) / scaleFactor).toFixed(numberOfDecimalPlacesOfHitCoordinates)),
            y: parseFloat(((centerFieldMarkers[0].y - hit.y) / scaleFactor).toFixed(numberOfDecimalPlacesOfHitCoordinates))
        };
    }

    const resetScannerToInitialState = () => {
        setCenterFieldMarkers([]);
        setHitsMarkers([]);
        setSelectedHitMarkerIndex(-1);
        setHitMarkerPlacementMode(false);
        setPointsScored([]);
        setHitDetectionError(null);
        setHitDetectorModelVersion(null);
    }

    const changeCenterFieldMarkerRadius = (event: React.ChangeEvent<HTMLInputElement>) => {
        const radius: number = parseInt(event.target.value);

        const updatedCirclesMarkers: CircleMarker[] = centerFieldMarkers.map((circleMarker: CircleMarker) =>
            ({ x: circleMarker.x, y: circleMarker.y, radius: radius }));
        updatedCirclesMarkers[1].radius = updatedCirclesMarkers[1].radius * innerCenterFieldMarkerScale;

        setCenterFieldMarkers(updatedCirclesMarkers);
    }

    const handleCenterFieldMarkerArrowButtonMove = (dx: number, dy: number) => {
        const updatedCirclesMarkers: CircleMarker[] = centerFieldMarkers.map((circleMarker: CircleMarker) =>
            ({ x: circleMarker.x + dx, y: circleMarker.y + dy, radius: circleMarker.radius }));

        setCenterFieldMarkers(updatedCirclesMarkers);
    };

    const handleHitMarkerArrowButtonMove = (dx: number, dy: number) => {
        const updatedHitMarkers: HitMarker[] = [...hitsMarkers];
        let selectedHitMarker: HitMarker = updatedHitMarkers[selectedHitMarkerIndex];

        selectedHitMarker = { ...selectedHitMarker, x: selectedHitMarker.x + dx, y: selectedHitMarker.y + dy }

        updatedHitMarkers[selectedHitMarkerIndex] = selectedHitMarker;

        setHitsMarkers(updatedHitMarkers);
    };

    const getNumberOfRatedShots = (): number => {
        return props.shotsPerTarget - props.testShotsPerTarget;
    }

    const getInfoMessage = () => {
        if (centerFieldMarkersRendered) {
            return `Jeśli automatyczne oznaczenie czarnego pola jest niedokładne (lub w ogóle się nie pojawiło), postaraj się je skorygować używając w pierwszej kolejności suwaka czułości skanowania. Wypróbuj kilka jego położeń obserwując cały czas czerwone oznaczenie. Jeśli nie udało się znaleźć w ten sposób dokładnego położenia, ustaw oznaczenie ręcznie w samym centrum tarczy, a za pomocą suwaka promienia dostosuj promień zaznaczenia tak, aby jak najdokładniej pokrywał się z brzegiem czarnego pola tarczy. Kiedy uznasz, że oznaczenie jest już odpowiednio ustawione, kliknij przycisk 'Zatwierdź'. 
            
Im dokładniej zostanie ustawiony promień i centrum oznaczenia, tym dokładniej zostaną obliczone punkty w kolejnym etapie.`;

        } else {
            return `Pomarańczowe oznaczenia wymagają szczególnego sprawdzenia. Jeśli wszystkie trafienia nie zostały poprawnie rozpoznane, ustaw brakujące oznaczenia ręcznie. Kliknij 'Dodaj oznaczenie', a następnie wskaż miejsce trafienia bezpośrednio na tarczy. Po kliknięciu w dowolne oznaczenie możesz użyć podręcznego panelu w celu dokładnego ustawienia położenia, dodania kolejnego oznaczenia lub usunięcia aktualnie wybranego.
                       
Kiedy uznasz, że oznaczenia są odpowiednio ustawione, kliknij przycisk 'Zatwierdź'.`;
        }
    }

    const acceptedDetectionsCount = hitsMarkers.filter((marker) => marker.detectionStatus === "ACCEPTED").length;
    const reviewDetectionsCount = hitsMarkers.filter((marker) => marker.detectionStatus === "REVIEW").length;
    const manualMarkersCount = hitsMarkers.filter((marker) => marker.detectionStatus === undefined).length;
    const previousStep = getPreviousScannerStep(scannerStep);
    const backButtonLabel = previousStep.phase === "session-data"
        ? "Wróć do ustawień sesji"
        : previousStep.phase === "center-field"
            ? "Wróć do czarnego pola"
            : "Wróć do poprzedniej tarczy";

    return (
        <Container fluid id="mainContainer">
            <Row>
                <Col md={7}>
                    <div id="scanningTitle">
                        {centerFieldMarkersRendered == true ? 'Detekcja czarnego pola' : 'Detekcja trafień'}
                        <span className="scanning-progress">Tarcza {currentImageIndex + 1} z {props.targetImages.length}</span>
                    </div>
                    <svg
                        id="circle-container"
                        className={hitMarkerPlacementMode ? "hit-marker-placement-mode" : ""}
                        onMouseDown={handleTargetMouseDown}
                        onMouseMove={handleMove}
                        onMouseUp={handleMouseUp}
                        onTouchStart={handleTargetTouchStart}
                        onTouchMove={handleTargetTouchMove}
                        onTouchEnd={handleTargetTouchEnd}
                        onTouchCancel={handleTargetTouchCancel}
                        ref={svgRef}
                    >
                        <image ref={targetImageRef} href={props.targetImages[currentImageIndex]} width="100%" />
                        {centerFieldMarkersRendered &&
                            centerFieldMarkers.map((circle: CircleMarker, index: number) => {
                                return (
                                    <circle
                                        key={`center-field-marker-${index}`}
                                        className="circle"
                                        cx={circle.x}
                                        cy={circle.y}
                                        r={circle.radius}
                                        fill="red"
                                        fillOpacity={0.0}
                                        stroke="red"
                                        strokeWidth="2"
                                        onMouseDown={centerFieldMarkerHandleMouseDown}
                                        onTouchStart={centerFieldMarkerHandleMouseDown}
                                    />
                                )
                            })
                        }
                        {centerFieldMarkersRendered && centerFieldMarkers.length != 0 && <g className="arrow-buttons" transform={`translate(${getCenterFieldOuterMarker().x}, ${getCenterFieldOuterMarker().y + 80})`}>
                            <g transform="translate(10, -30)" onClick={(e) => { e.preventDefault(); handleCenterFieldMarkerArrowButtonMove(0, -1) }}>
                                <rect width={30} height={30} fill="lightblue" rx="5" ry="5" />
                                <text x="15" y="20" fontSize="16" textAnchor="middle" fill="black">↑</text>
                            </g>
                            <g transform="translate(10, 30)" onClick={(e) => { e.preventDefault(); handleCenterFieldMarkerArrowButtonMove(0, 1) }}>
                                <rect width={30} height={30} fill="lightblue" rx="5" ry="5" />
                                <text x="15" y="20" fontSize="16" textAnchor="middle" fill="black">↓</text>
                            </g>
                            <g transform="translate(-20, 0)" onClick={(e) => { e.preventDefault(); handleCenterFieldMarkerArrowButtonMove(-1, 0) }}>
                                <rect width={30} height={30} fill="lightblue" rx="5" ry="5" />
                                <text x="15" y="20" fontSize="16" textAnchor="middle" fill="black">←</text>
                            </g>
                            <g transform="translate(40, 0)" onClick={(e) => { e.preventDefault(); handleCenterFieldMarkerArrowButtonMove(1, 0) }}>
                                <rect width={30} height={30} fill="lightblue" rx="5" ry="5" />
                                <text x="15" y="20" fontSize="16" textAnchor="middle" fill="black">→</text>
                            </g>
                        </g>}
                        {hitsMarkers.map((circle: HitMarker, index: number) => {
                            const isSelected = index === selectedHitMarkerIndex;

                            return (
                                <circle
                                    key={index}
                                    className={`circle hit-marker ${getHitMarkerStatusClassName(circle.detectionStatus)}${isSelected ? " hit-marker--selected" : ""}`}
                                    data-detection-status={circle.detectionStatus ?? "MANUAL"}
                                    aria-label={circle.detectionStatus === "REVIEW"
                                        ? "Trafienie do sprawdzenia"
                                        : circle.detectionStatus === "ACCEPTED" ? "Zaakceptowane trafienie" : "Ręcznie dodane trafienie"}
                                    cx={circle.x}
                                    cy={circle.y}
                                    r={circle.radius}
                                    fillOpacity={0.0}
                                    onMouseDown={hitMarkerHandleMouseDown(index)}
                                    onTouchStart={hitMarkerHandleMouseDown(index)}
                                />
                            )
                        })}

                        {selectedHitMarkerIndex != -1 && <g className="arrow-buttons" transform={`translate(${hitsMarkers[selectedHitMarkerIndex].x}, ${hitsMarkers[selectedHitMarkerIndex].y - 80})`}>
                            {/* Up Arrow Button */}
                            <g transform="translate(10, -30)" onClick={(e) => { e.preventDefault(); handleHitMarkerArrowButtonMove(0, -1) }}>
                                <rect width={30} height={30} fill="lightblue" rx="5" ry="5" />
                                <text x="15" y="20" fontSize="16" textAnchor="middle" fill="black">↑</text>
                            </g>
                            {/* Down Arrow Button */}
                            <g transform="translate(10, 30)" onClick={(e) => { e.preventDefault(); handleHitMarkerArrowButtonMove(0, 1) }}>
                                <rect width={30} height={30} fill="lightblue" rx="5" ry="5" />
                                <text x="15" y="20" fontSize="16" textAnchor="middle" fill="black">↓</text>
                            </g>

                            {/* Left Arrow Button */}
                            <g transform="translate(-20, 0)" onClick={(e) => { e.preventDefault(); handleHitMarkerArrowButtonMove(-1, 0) }}>
                                <rect width={30} height={30} fill="lightblue" rx="5" ry="5" />
                                <text x="15" y="20" fontSize="16" textAnchor="middle" fill="black">←</text>
                            </g>

                            {/* Right Arrow Button */}
                            <g transform="translate(40, 0)" onClick={(e) => { e.preventDefault(); handleHitMarkerArrowButtonMove(1, 0) }}>
                                <rect width={30} height={30} fill="lightblue" rx="5" ry="5" />
                                <text x="15" y="20" fontSize="16" textAnchor="middle" fill="black">→</text>
                            </g>

                            {/* Points label */}
                            <g transform="translate(11, 1)" onClick={(e) => { e.preventDefault(); }}>
                                <rect width={28} height={28} fill="lightblue" rx="5" ry="5" />
                                <text x="14" y="20" fontSize="17" fontWeight="bold" textAnchor="middle" fill="green">{selectedHitMarkerPoints}</text>
                            </g>

                            {/* Add hit marker Button */}
                            <g transform="translate(-55, -30)" onClick={() => startHitMarkerPlacement()}>
                                <circle cx="15" cy="15" r="12" fill="green" />
                                <text x="15" y="20" fontSize="18" textAnchor="middle" fill="white" fontWeight="bold">+</text>
                            </g>

                            {/* Delete hit marker Button */}
                            <g transform="translate(-55, 30)" onClick={() => deleteHitMarker()}>
                                <circle cx="15" cy="15" r="12" fill="darkred" />
                                <text x="15" y="20" fontSize="18" textAnchor="middle" fill="white" fontWeight="bold">-</text>
                            </g>

                            {/* Close Button */}
                            <g transform="translate(40, -58)" onClick={() => { setSelectedHitMarkerIndex(-1) }}>
                                <circle cx="15" cy="15" r="12" fill="blue" />
                                <text x="15" y="20" fontSize="18" textAnchor="middle" fill="white" fontWeight="bold">x</text>
                            </g>
                        </g>}

                    </svg>

                </Col>
                <Col id="scoreBox" md={1}>
                    <div className="d-flex flex-row flex-wrap">
                        {pointsScored.map((points: string, index: number) => {
                            return (
                                <div key={`scored-points-${index}`} id="detectedScoredPointsBox">{points}</div>
                            )
                        })}
                    </div>
                    <hr id="sumLine" />
                    <div className="d-flex">
                        {centerFieldMarkersRendered || <div id="totalScoreBox">{calculateTotalScoreSum(pointsScored)}</div>}
                    </div>
                </Col>
                <Col id="controlPanel" md={4}>
                    {centerFieldMarkersRendered ||
                        <div style={{ fontSize: "26px", marginBottom: "" }}>Wykryte trafienia:
                            <span style={{ color: hitsMarkers.length == props.shotsPerTarget ? "green" : "red", fontSize: "28px" }}> {hitsMarkers.length}/{props.shotsPerTarget}</span>
                        </div>}
                    {!centerFieldMarkersRendered && props.competitionMode &&
                        <div style={{ fontSize: "26px", marginBottom: "" }}>Strzały oceniane:
                            <span style={{ color: "#007bff", fontSize: "28px" }}> {getNumberOfRatedShots()}</span>
                        </div>}
                    {!centerFieldMarkersRendered && (
                        <div className="hit-detection-summary" aria-label="Podsumowanie automatycznej detekcji">
                            <span className="hit-detection-summary__accepted">Duża pewność: {acceptedDetectionsCount}</span>
                            <span className="hit-detection-summary__review">Do sprawdzenia: {reviewDetectionsCount}</span>
                            {manualMarkersCount > 0 && <span className="hit-detection-summary__manual">Ręczne: {manualMarkersCount}</span>}
                        </div>
                    )}
                    {!centerFieldMarkersRendered && hitDetectionError && (
                        <div className="hit-detection-error" role="alert">{hitDetectionError}</div>
                    )}
                    {centerFieldMarkersRendered && (
                        <div>
                            <input id="scanningSensitivityOffsetSlider" type="range" min="-90" max="90" step="1"
                                value={centerFieldScanningSensivityOffset} onChange={e => {
                                    setCenterFieldScanningSensivityOffset(parseInt(e.target.value));
                                    if (detectCenterFieldTimeoutRef.current) {
                                        clearTimeout(detectCenterFieldTimeoutRef.current);
                                    }
                                    detectCenterFieldTimeoutRef.current = setTimeout(() => {
                                        detectCenterField(props.targetImages[currentImageIndex]);
                                    }, 300);
                                }} />
                            <label htmlFor="scanningSensitivityOffsetSlider">Czułość skanowania</label>
                        </div>
                    )}
                    {centerFieldMarkersRendered && centerFieldMarkers.length != 0 &&
                        (<div>
                            <input id="centerFieldMarkerRadiusOffsetSlider" type="range"
                                min={svgRef && svgRef.current ? (svgRef.current.width.baseVal.value * 0.12) : 400}
                                max={svgRef && svgRef.current ? (svgRef.current.width.baseVal.value * 0.49) : 400}
                                step="0.5"
                                value={getCenterFieldOuterMarker().radius} onChange={e => changeCenterFieldMarkerRadius(e)} />
                            <label htmlFor="scanningSensitivityOffsetSlider">Promień oznaczenia</label>
                        </div>)}
                    {centerFieldMarkersRendered || (
                        <>
                            <Button className="control-panel-btn" variant={hitMarkerPlacementMode ? "warning" : "primary"} onClick={() => toggleHitMarkerPlacement()}>
                                {hitMarkerPlacementMode ? "Anuluj dodawanie" : "Dodaj oznaczenie"}
                            </Button>
                            {hitMarkerPlacementMode && (
                                <div className="manual-hit-placement-help">
                                    Kliknij lub dotknij miejsce trafienia na tarczy.
                                </div>
                            )}
                        </>
                    )}
                    {centerFieldMarkersRendered || <Button className="control-panel-btn" disabled={selectedHitMarkerIndex === -1} onClick={() => deleteHitMarker()}>Usuń oznaczenie</Button>}
                    {centerFieldMarkersRendered || <Button className="control-panel-btn" variant="danger" disabled={hitsMarkers.length === 0} onClick={() => deleteAllHitMarkers()}>Usuń wszystkie oznaczenia</Button>}
                    <Button className="control-panel-btn" variant="info" onClick={() => showInfo({ message: getInfoMessage() })}>Pomoc</Button>
                    <Button size="lg" className="control-panel-btn" onClick={() => submit()}>Zatwierdź</Button>
                    <Button className="control-panel-btn" variant="secondary" onClick={goBack}>{backButtonLabel}</Button>
                    {previousStep.phase !== "session-data" && (
                        <Button className="control-panel-btn" variant="outline-secondary" onClick={goBackToSessionData}>Edytuj ustawienia sesji</Button>
                    )}
                </Col>
            </Row>

        </Container>
    );
};


export default TargetScanner;
